/**
 * API Key Types for External API Access
 *
 * API keys provide programmatic access to SimCheck.ai APIs
 * for external developers, partners, and automation.
 */
/**
 * Curated MCP permission groups
 * These map to underlying ApiKeyScopes but provide a simpler UX
 */
export const MCP_PERMISSION_GROUPS = [
    {
        id: 'read_only',
        displayName: 'Read Only',
        description: 'View flows, test results, locations, networks, reports, and artifacts. Cannot execute tests.',
        icon: 'Eye',
        scopes: [
            'flows:read',
            'tests:read',
            'schedules:read',
            'esim:read',
            'locations:read',
            'devices:read',
            'clusters:read',
            'organization:read',
            'networks:read',
            'executions:read',
            'reports:read',
            'artifacts:read'
        ]
    },
    {
        id: 'execute_flows',
        displayName: 'Execute Flows',
        description: 'All read permissions plus ability to run test flows, create schedules, and execute tests.',
        icon: 'Play',
        scopes: [
            'flows:read',
            'flows:execute',
            'tests:read',
            'tests:execute',
            'schedules:read',
            'schedules:write',
            'esim:read',
            'locations:read',
            'devices:read',
            'clusters:read',
            'organization:read',
            'networks:read',
            'executions:read',
            'executions:write',
            'reports:read',
            'reports:write',
            'artifacts:read',
            'shares:create'
        ]
    },
    {
        id: 'full_access',
        displayName: 'Full Access',
        description: 'Complete access to all MCP operations including schedules and configuration.',
        icon: 'ShieldCheck',
        scopes: ['admin:*']
    }
];
/**
 * Get MCP permission group by ID
 */
export function getMcpPermissionGroup(groupId) {
    return MCP_PERMISSION_GROUPS.find(g => g.id === groupId);
}
/**
 * Get scopes for an MCP permission group
 */
export function getMcpPermissionGroupScopes(groupId) {
    const group = getMcpPermissionGroup(groupId);
    return group?.scopes || [];
}
/**
 * Standard rate limit configurations
 */
export const RATE_LIMIT_CONFIGS = {
    standard: {
        tier: 'standard',
        requestsPerMinute: 60,
        requestsPerHour: 1000,
        requestsPerDay: 10000,
    },
    elevated: {
        tier: 'elevated',
        requestsPerMinute: 300,
        requestsPerHour: 5000,
        requestsPerDay: 50000,
    },
    unlimited: {
        tier: 'unlimited',
        requestsPerMinute: -1, // -1 = unlimited
        requestsPerHour: -1,
        requestsPerDay: -1,
    },
};
/**
 * Rate limit configurations for Firebase Auth users
 * Higher limits than API keys since UI is more interactive
 */
export const USER_RATE_LIMIT_CONFIGS = {
    regular: {
        tier: 'regular',
        requestsPerMinute: 120, // 2x API key standard
        requestsPerHour: 2000,
        requestsPerDay: 20000,
    },
    admin: {
        tier: 'admin',
        requestsPerMinute: 300,
        requestsPerHour: 5000,
        requestsPerDay: 50000,
    },
    super_admin: {
        tier: 'super_admin',
        requestsPerMinute: -1, // -1 = unlimited
        requestsPerHour: -1,
        requestsPerDay: -1,
    },
};
// ============================================================================
// Scope Definitions for UI
// ============================================================================
/**
 * All available scopes with their definitions
 */
export const API_KEY_SCOPE_DEFINITIONS = [
    // Flows
    { scope: 'flows:read', category: 'flows', displayName: 'Read Flows', description: 'View flow definitions and details', isPrivileged: false },
    { scope: 'flows:write', category: 'flows', displayName: 'Write Flows', description: 'Create and update flow definitions', isPrivileged: false },
    { scope: 'flows:execute', category: 'flows', displayName: 'Execute Flows', description: 'Run flow executions', isPrivileged: false },
    { scope: 'flows:delete', category: 'flows', displayName: 'Delete Flows', description: 'Delete flow definitions', isPrivileged: true },
    // Tests
    { scope: 'tests:read', category: 'tests', displayName: 'Read Tests', description: 'View test configurations and runs', isPrivileged: false },
    { scope: 'tests:write', category: 'tests', displayName: 'Write Tests', description: 'Create and update test configurations', isPrivileged: false },
    { scope: 'tests:execute', category: 'tests', displayName: 'Execute Tests', description: 'Run test executions', isPrivileged: false },
    { scope: 'tests:delete', category: 'tests', displayName: 'Delete Tests', description: 'Delete test configurations and runs', isPrivileged: true },
    // Schedules
    { scope: 'schedules:read', category: 'schedules', displayName: 'Read Schedules', description: 'View test schedules', isPrivileged: false },
    { scope: 'schedules:write', category: 'schedules', displayName: 'Write Schedules', description: 'Create and update schedules', isPrivileged: false },
    { scope: 'schedules:delete', category: 'schedules', displayName: 'Delete Schedules', description: 'Delete schedules', isPrivileged: true },
    // eSIM
    { scope: 'esim:read', category: 'esim', displayName: 'Read eSIM', description: 'View eSIM profiles, groups, and networks', isPrivileged: false },
    { scope: 'esim:write', category: 'esim', displayName: 'Write eSIM', description: 'Create and update eSIM profiles', isPrivileged: false },
    { scope: 'esim:batch', category: 'esim', displayName: 'Batch Import eSIM', description: 'Bulk import eSIM profiles', isPrivileged: false },
    { scope: 'esim:delete', category: 'esim', displayName: 'Delete eSIM', description: 'Delete eSIM profiles', isPrivileged: true },
    // Locations
    { scope: 'locations:read', category: 'locations', displayName: 'Read Locations', description: 'View locations, hierarchy, and availability', isPrivileged: false },
    // Devices
    { scope: 'devices:read', category: 'devices', displayName: 'Read Devices', description: 'View edge devices and modems', isPrivileged: false },
    { scope: 'devices:command', category: 'devices', displayName: 'Send Commands', description: 'Send AT commands to modems', isPrivileged: true },
    // Clusters
    { scope: 'clusters:read', category: 'clusters', displayName: 'Read Clusters', description: 'View location clusters', isPrivileged: false },
    { scope: 'clusters:write', category: 'clusters', displayName: 'Write Clusters', description: 'Create and update clusters', isPrivileged: false },
    { scope: 'clusters:delete', category: 'clusters', displayName: 'Delete Clusters', description: 'Delete clusters', isPrivileged: true },
    // Webhooks
    { scope: 'webhooks:read', category: 'webhooks', displayName: 'Read Webhooks', description: 'View webhook configurations', isPrivileged: false },
    { scope: 'webhooks:write', category: 'webhooks', displayName: 'Write Webhooks', description: 'Create and update webhooks', isPrivileged: false },
    { scope: 'webhooks:delete', category: 'webhooks', displayName: 'Delete Webhooks', description: 'Delete webhooks', isPrivileged: true },
    // Organization
    { scope: 'organization:read', category: 'organization', displayName: 'Read Organization', description: 'View organization information', isPrivileged: false },
    // Reports (reports redesign)
    { scope: 'reports:read', category: 'reports', displayName: 'Read Reports', description: 'Generate and view reports (SLA grid, operator benchmarking, location health, etc.)', isPrivileged: false },
    { scope: 'reports:write', category: 'reports', displayName: 'Write Reports', description: 'Create, update, and schedule report definitions', isPrivileged: false },
    // Artifacts (reports redesign)
    { scope: 'artifacts:read', category: 'artifacts', displayName: 'Read Artifacts', description: 'Fetch signed URLs for execution artifacts (PCAP, AT log, voice samples, screenshots)', isPrivileged: false },
    // Shares (reports redesign)
    { scope: 'shares:create', category: 'shares', displayName: 'Create Share Links', description: 'Create shareable report links programmatically', isPrivileged: false },
    // Admin
    { scope: 'admin:*', category: 'admin', displayName: 'Full Admin Access', description: 'Complete access to all operations', isPrivileged: true },
];
/**
 * Get scopes by category
 */
export function getScopesByCategory(category) {
    return API_KEY_SCOPE_DEFINITIONS.filter(s => s.category === category);
}
/**
 * Check if a scope allows an action
 * Handles wildcards like 'admin:*'
 */
export function scopeAllowsAction(scopes, requiredScope) {
    // Check for admin wildcard
    if (scopes.includes('admin:*')) {
        return true;
    }
    // Check for exact match
    if (scopes.includes(requiredScope)) {
        return true;
    }
    // Check for category wildcard (e.g., 'flows:*' allows 'flows:read')
    const [category] = requiredScope.split(':');
    const wildcardScope = `${category}:*`;
    if (scopes.includes(wildcardScope)) {
        return true;
    }
    return false;
}
/**
 * Get all categories
 */
export function getScopeCategories() {
    return [
        'flows',
        'tests',
        'schedules',
        'esim',
        'locations',
        'devices',
        'clusters',
        'webhooks',
        'organization',
        'reports',
        'artifacts',
        'shares',
        'admin',
    ];
}
/**
 * All available system scopes with their definitions
 */
export const SYSTEM_API_KEY_SCOPE_DEFINITIONS = [
    // Cross-org read operations
    { scope: 'system:read_all_orgs', category: 'system', displayName: 'Read All Orgs', description: 'View all organizations in the platform' },
    { scope: 'system:read_all_executions', category: 'system', displayName: 'Read All Executions', description: 'View test executions across all organizations' },
    { scope: 'system:read_all_users', category: 'system', displayName: 'Read All Users', description: 'View all users in the platform' },
    { scope: 'system:read_metrics', category: 'system', displayName: 'Read Metrics', description: 'View platform-wide metrics and analytics' },
    { scope: 'system:read_audit_logs', category: 'system', displayName: 'Read Audit Logs', description: 'View all audit logs across the platform' },
    // Monitoring operations
    { scope: 'system:health_check', category: 'system', displayName: 'Health Check', description: 'Access health check endpoints' },
    { scope: 'system:status', category: 'system', displayName: 'System Status', description: 'View system status and diagnostics' },
    // Maintenance operations
    { scope: 'system:manage_locks', category: 'system', displayName: 'Manage Locks', description: 'Manage resource locks across the platform' },
    { scope: 'system:manage_queue', category: 'system', displayName: 'Manage Queue', description: 'Manage execution queue operations' },
    { scope: 'system:manage_settings', category: 'system', displayName: 'Manage Settings', description: 'Read and update system settings' },
    // Full access
    { scope: 'system:*', category: 'system', displayName: 'Full System Access', description: 'Complete access to all system operations (super admin equivalent)' },
];
/**
 * Check if a system scope allows an action
 * Handles wildcard 'system:*'
 */
export function systemScopeAllowsAction(scopes, requiredScope) {
    // Check for system wildcard
    if (scopes.includes('system:*')) {
        return true;
    }
    // Check for exact match
    return scopes.includes(requiredScope);
}
/**
 * Check if an API key is a system key
 */
export function isSystemApiKey(apiKey) {
    return apiKey.type === 'system';
}
/**
 * Check if an API key is an MCP key
 */
export function isMcpKey(apiKey) {
    return apiKey.type === 'mcp_organization' || apiKey.type === 'mcp_system';
}
/**
 * Check if an API key is an MCP organization key
 */
export function isMcpOrganizationKey(apiKey) {
    return apiKey.type === 'mcp_organization';
}
/**
 * Check if an API key is an MCP system key
 */
export function isMcpSystemKey(apiKey) {
    return apiKey.type === 'mcp_system';
}
